import React, { useState } from 'react';
import { apiCall } from '../services/api';
import ScriptGenerator from './ScriptGenerator';
import RecommendationsPanel from './RecommendationsPanel';
import ActionItems from './ActionItems';
import ContentGapsVisualization from './ContentGapsVisualization';
import RankingFactorsChart from './RankingFactorsChart';
import TitleVariations from './TitleVariations';
import ExportPanel from './ExportPanel';
import '../styles/AdvancedAnalysis.css';

export default function AdvancedAnalysis({ user, onCreditsUpdate }) {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [userCredits, setUserCredits] = useState(user?.credits || 0);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!keyword.trim()) {
      setError('Please enter a keyword');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      console.log('🚀 Advanced Analysis for:', keyword);

      const response = await apiCall('/analysis/advanced-analysis', 'POST', {
        keyword: keyword.trim(),
        generateScript: true
      });

      console.log('✅ Results:', response);

      if (response.credits) {
        setUserCredits(response.credits.remaining);
        if (onCreditsUpdate) {
          onCreditsUpdate(response.credits.remaining);
        }
        console.log(`💳 Credits: ${response.credits.used} deducted, ${response.credits.remaining} remaining`);
      }

      // FIX: Extract data from response
      const analysisData = response.data || response;
      setResults(analysisData);
      setActiveTab('overview');
    } catch (err) {
      console.error('❌ Error:', err);

      if (err.message === 'INSUFFICIENT_CREDITS' && err.details) {
        const { required, available } = err.details;
        setError(`❌ Insufficient credits. Need: ${required}, Available: ${available}. Buy more at dashboard.`);
      } else {
        setError(err.message || 'Analysis failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="advanced-analysis-container">
      <div className="analysis-header">
        <h1>🎯 YouTube Content Strategy Analyzer</h1>
        <p>भारतीय YouTubers के लिए डेटा-driven content strategy</p>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <form onSubmit={handleSearch}>
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Enter YouTube keyword (e.g., 'web development for beginners')"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              disabled={loading}
              className="main-search-input"
            />
            <button type="submit" disabled={loading} className="analyze-btn">
              {loading ? '⏳ Analyzing...' : '🔍 Analyze'}
            </button>
          </div>

          <div className="credit-info-bar">
            <span className="credit-cost">💳 20 credits per analysis</span>
            <span className="credit-available">Your balance: <strong>{userCredits}</strong> credits</span>
          </div>
        </form>

        {error && <div className="error-alert">❌ {error}</div>}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Analyzing top videos and generating content strategy...</p>
          <div className="progress-steps">
            <div className="step active">Fetching videos</div>
            <div className="step">Analyzing content gaps</div>
            <div className="step">Mining comments</div>
            <div className="step">Generating script</div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {results && (
        <div className="results-container">
          <div className="results-header">
            <h2>📊 Analysis Results for "{keyword}"</h2>
          </div>

          {/* Tabs Navigation */}
          <div className="tabs-navigation">
            <button
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              📊 Overview
            </button>
            <button
              className={`tab ${activeTab === 'gaps' ? 'active' : ''}`}
              onClick={() => setActiveTab('gaps')}
            >
              🔍 Content Gaps
            </button>
            <button
              className={`tab ${activeTab === 'recommendations' ? 'active' : ''}`}
              onClick={() => setActiveTab('recommendations')}
            >
              💡 Recommendations
            </button>
            <button
              className={`tab ${activeTab === 'script' ? 'active' : ''}`}
              onClick={() => setActiveTab('script')}
            >
              ✍️ Script
            </button>
            <button
              className={`tab ${activeTab === 'ranking' ? 'active' : ''}`}
              onClick={() => setActiveTab('ranking')}
            >
              📈 Ranking Factors
            </button>
            <button
              className={`tab ${activeTab === 'titles' ? 'active' : ''}`}
              onClick={() => setActiveTab('titles')}
            >
              📝 Titles
            </button>
            <button
              className={`tab ${activeTab === 'actions' ? 'active' : ''}`}
              onClick={() => setActiveTab('actions')}
            >
              ✅ Action Items
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'overview' && (
              <div className="overview-tab">
                <h3>📊 Analysis Overview</h3>
                <div className="metrics-grid">
                  <div className="metric-card">
                    <div className="metric-icon">📹</div>
                    <div className="metric-value">{results?.videoAnalysis?.totalAnalyzed || 0}</div>
                    <div className="metric-label">Videos Analyzed</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-icon">🎯</div>
                    <div className="metric-value">{results?.contentGaps?.missingSubtopics?.length || 0}</div>
                    <div className="metric-label">Content Gaps Found</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-icon">❓</div>
                    <div className="metric-value">{results?.contentGaps?.unansweredQuestions?.length || 0}</div>
                    <div className="metric-label">Questions Identified</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-icon">💡</div>
                    <div className="metric-value">{results?.recommendations?.length || 0}</div>
                    <div className="metric-label">Recommendations</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'gaps' && <ContentGapsVisualization data={results} />}
            {activeTab === 'recommendations' && <RecommendationsPanel data={results} />}
            {activeTab === 'script' && <ScriptGenerator data={results} />}
            {activeTab === 'ranking' && <RankingFactorsChart data={results} />}
            {activeTab === 'titles' && <TitleVariations data={results} />}
            {activeTab === 'actions' && <ActionItems data={results} />}
          </div>

          {/* Export Panel */}
          <ExportPanel data={results} keyword={keyword} />
        </div>
      )}
    </div>
  );
}
